import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Alert, Button, Badge, Modal, Spinner, Tab, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileAlt, 
  faCheckCircle, 
  faExclamationTriangle,
  faLightbulb,
  faQuestionCircle,
  faChartBar,
  faBrain,
  faArrowRight,
  faClipboardCheck,
  faProjectDiagram,
  faStar,
  faRobot,
  faLink,
  faChevronRight,
  faMagic,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import SmartDocumentChecklistProduction from '../SmartDocumentChecklistProduction';
import { Form, Row as BsRow, Col as BsCol, ProgressBar } from 'react-bootstrap';
import './WorkflowIntelligenceTab.css';
import './AIPatterns.enhanced.css';

interface WorkflowIntelligenceTabProps {
  brokerageId?: string;
}

interface Document {
  name: string;
  status: 'complete' | 'missing';
  required: boolean;
}

interface Workflow {
  id: string;
  name: string;
  confidence: number;
  documents: Document[];
  tip?: string;
  usageCount: number;
  successRate: number;
  timeSaved: number;
  topAgentsCount: number;
  dateRange: string;
}

interface Relationship {
  id?: string | number;
  primaryDoc: string;
  relatedDoc: string;
  primary_doc?: string;
  related_doc?: string;
  confidence: number;
  relationship?: string;
  usage_count?: number;
}

interface Insights {
  documentsAnalyzed: number;
  agentsCount: number;
  patternsFound: number;
  timeSaved: number;
  recentLearning: string[];
  lastUpdated: Date;
}

const WorkflowIntelligenceTab: React.FC<WorkflowIntelligenceTabProps> = ({ brokerageId }) => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [insights, setInsights] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(true);
  const [showWhyModal, setShowWhyModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  
  // Transaction context state - matches Smart Checklist
  const [transactionType, setTransactionType] = useState<string>('purchase');
  const [agentRole, setAgentRole] = useState<string>('buyer_agent');
  
  // Filter relationships based on transaction context
  const getFilteredRelationships = () => {
    if (!relationships || relationships.length === 0) return [];
    
    // Filter based on transaction type and role
    return relationships.filter(rel => {
      const primary = (rel.primaryDoc || '').toLowerCase();
      const related = (rel.relatedDoc || '').toLowerCase();
      
      // Purchase-specific documents
      if (transactionType === 'purchase') {
        const purchaseDocs = ['purchase', 'buyer', 'loan', 'inspection', 'appraisal'];
        return purchaseDocs.some(term => primary.includes(term) || related.includes(term));
      }
      
      // Sale-specific documents
      if (transactionType === 'sale') {
        const saleDocs = ['listing', 'seller', 'disclosure', 'marketing'];
        return saleDocs.some(term => primary.includes(term) || related.includes(term));
      }
      
      // Rental-specific documents
      if (transactionType === 'rental') {
        const rentalDocs = ['lease', 'rental', 'tenant', 'landlord', 'deposit'];
        return rentalDocs.some(term => primary.includes(term) || related.includes(term));
      }
      
      // Referral-specific documents
      if (transactionType === 'referral') {
        const referralDocs = ['referral', 'commission', 'fee'];
        return referralDocs.some(term => primary.includes(term) || related.includes(term));
      }
      
      // Land contract documents
      if (transactionType === 'land_contract') {
        const landDocs = ['land', 'contract', 'deed', 'survey'];
        return landDocs.some(term => primary.includes(term) || related.includes(term));
      }
      
      return true; // Show all if no specific filter
    });
  };

  useEffect(() => {
    console.log('🔍 WorkflowIntelligenceTab - brokerageId changed:', brokerageId);
    if (brokerageId) {
      fetchWorkflowIntelligence();
    } else {
      console.log('⚠️ No brokerageId provided to WorkflowIntelligenceTab');
    }
  }, [brokerageId]);

  const fetchWorkflowIntelligence = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching workflow intelligence for brokerage:', brokerageId);
      
      // Fetch data from multiple sources
      let workflowRes, insightRes;
      try {
        [workflowRes, insightRes] = await Promise.all([
          axiosInstance.get(`/docmaster/workflows/${brokerageId}`),
          axiosInstance.get(`/docmaster/insights/${brokerageId}`)
        ]);
      } catch (err) {
        console.log('⚠️ Workflow/insights endpoints not available, using defaults');
        workflowRes = { data: [] };
        insightRes = { data: null };
      }
      
      // Fetch relationships from new DocMaster endpoint
      console.log('📊 Fetching relationships from:', `/broker-docmaster/relationships/${brokerageId}`);
      const relationshipRes = await axiosInstance.get(`/broker-docmaster/relationships/${brokerageId}`);

      setWorkflows(workflowRes.data);
      
      console.log('📥 Relationship response:', {
        status: relationshipRes.status,
        success: relationshipRes.data?.success,
        totalRelationships: relationshipRes.data?.totalRelationships,
        relationshipKeys: relationshipRes.data?.relationships ? Object.keys(relationshipRes.data.relationships) : [],
        fullData: relationshipRes.data
      });
      
      // Process relationships from new format
      if (relationshipRes.data.success && relationshipRes.data.relationships) {
        // Convert grouped relationships to flat array for display
        const flatRelationships: Relationship[] = [];
        const groups = relationshipRes.data.relationships;
        console.log('🔗 Relationship groups:', groups);
        
        Object.entries(groups).forEach(([type, rels]) => {
          if (Array.isArray(rels)) {
            rels.forEach((rel: any) => {
              flatRelationships.push({
                id: rel.id,
                primaryDoc: rel.primary || rel.primary_doc || '',
                relatedDoc: rel.related || rel.related_doc || '',
                primary_doc: rel.primary,
                related_doc: rel.related,
                relationship: type,
                confidence: rel.confidence || 0,
                usage_count: rel.usageCount || 1
              });
            });
          }
        });
        
        console.log(`📊 Processed ${flatRelationships.length} relationships`);
        setRelationships(flatRelationships);
      } else {
        console.log('⚠️ No relationships found in response or unexpected format');
        setRelationships(relationshipRes.data.relationships || relationshipRes.data || []);
      }
      
      setInsights(insightRes.data);
    } catch (error) {
      console.error('Error fetching workflow intelligence:', error);
    } finally {
      setLoading(false);
    }
  };

  const showWhyExplanation = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    setShowWhyModal(true);
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 80) return '#52c41a';
    if (confidence >= 60) return '#faad14';
    return '#1890ff';
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" style={{ color: '#00d4ff' }} />
        <p className="mt-3 text-muted">Loading workflow intelligence...</p>
      </div>
    );
  }

  return (
    <div className="workflow-intelligence-tab">
      <Tab.Container defaultActiveKey="checklist">
        <Nav variant="tabs" className="ai-nav-tabs">
          <Nav.Item>
            <Nav.Link eventKey="checklist">
              <FontAwesomeIcon icon={faClipboardCheck} className="ai-nav-icon" />
              <span>Smart Checklist</span>
              <Badge className="ms-2">NEW</Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content className="ai-tab-content">
          {/* AI Patterns tab removed - not functional */}
          
          <Tab.Pane eventKey="checklist">
            <Card className="border-0" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <FontAwesomeIcon icon={faClipboardCheck} className="me-2" style={{ color: '#00d4ff', fontSize: '24px' }} />
                  <div>
                    <h5 className="mb-0">Smart Document Checklist</h5>
                    <p className="mb-0 text-muted small">
                      Get intelligent document requirements based on property and transaction details
                    </p>
                  </div>
                </div>
                
                <Alert 
                  className="mb-4" 
                  style={{
                    background: 'linear-gradient(135deg, rgba(13, 27, 42, 0.95) 0%, rgba(27, 38, 59, 0.95) 100%)',
                    border: '1px solid #00d4ff',
                    color: '#ffffff',
                    boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)'
                  }}
                >
                  <FontAwesomeIcon icon={faLightbulb} className="me-2" style={{ color: '#00d4ff' }} />
                  <strong>Pro Tip:</strong> Update the property and transaction details below to see which documents are required for your specific deal.
                </Alert>

                <SmartDocumentChecklistProduction 
                  brokerageId={brokerageId || ''}
                  onDocumentSelect={(documentId) => {
                    console.log('Document selected:', documentId);
                    // TODO: Integrate with document template system
                    alert(`Opening template for: ${documentId}`);
                  }}
                />
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Why Modal */}
      <Modal
        show={showWhyModal}
        onHide={() => setShowWhyModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{ background: 'rgba(255, 255, 255, 0.95)', border: 'none' }}>
          <Modal.Title>Why This Workflow?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'rgba(255, 255, 255, 0.95)' }}>
          {selectedWorkflow && (
            <div>
              <h5>Based on analysis of your brokerage:</h5>
              <ul className="mt-3">
                <li className="mb-2">✓ Used successfully {selectedWorkflow.usageCount} times</li>
                <li className="mb-2">✓ {selectedWorkflow.successRate}% completion rate</li>
                <li className="mb-2">✓ Saves average {selectedWorkflow.timeSaved} minutes</li>
                <li className="mb-2">✓ Your top {selectedWorkflow.topAgentsCount} agents follow this pattern</li>
              </ul>
              <p className="text-muted mt-3">
                Data from: {selectedWorkflow.dateRange}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: 'rgba(255, 255, 255, 0.95)', border: 'none' }}>
          <Button variant="primary" onClick={() => setShowWhyModal(false)}>
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WorkflowIntelligenceTab;